/**
 * 国际化Hook
 */

import { useTranslation } from 'react-i18next';
import i18n from '@/locales';

export interface UseI18nResult {
  // 翻译函数
  t: (key: string, options?: any) => string;
  
  // 当前语言
  language: string;
  
  // 切换语言
  changeLanguage: (lng: string) => Promise<void>;
  
  // 是否正在加载
  isLoading: boolean;
  
  // 是否准备就绪
  ready: boolean;
}

/**
 * 国际化Hook
 */
export const useI18n = (): UseI18nResult => {
  const { t, i18n: i18nInstance, ready } = useTranslation();

  const changeLanguage = async (lng: string): Promise<void> => {
    try {
      await i18nInstance.changeLanguage(lng);
      // 存储到localStorage
      localStorage.setItem('tenant-language', lng);
      // 更新HTML lang属性
      document.documentElement.lang = lng;
    } catch (error) {
      console.error('Failed to change language:', error);
      throw error;
    }
  };

  return {
    t,
    language: i18nInstance.language,
    changeLanguage,
    isLoading: !ready,
    ready,
  };
};

/**
 * 格式化翻译函数
 */
export const useFormatMessage = () => {
  const { t } = useI18n();

  return {
    // 格式化消息
    formatMessage: (key: string, values?: Record<string, any>) => {
      return t(key, values);
    },

    // 格式化带插值的消息
    formatMessageWithValues: (key: string, values: Record<string, any>) => {
      return t(key, values);
    },

    // 格式化数字
    formatNumber: (number: number, locale?: string) => {
      const currentLocale = locale || i18n.language;
      return new Intl.NumberFormat(currentLocale).format(number);
    },

    // 格式化货币
    formatCurrency: (amount: number, currency = 'CNY', locale?: string) => {
      const currentLocale = locale || i18n.language;
      return new Intl.NumberFormat(currentLocale, {
        style: 'currency',
        currency,
      }).format(amount);
    },

    // 格式化日期
    formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions, locale?: string) => {
      const currentLocale = locale || i18n.language;
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      
      const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };

      return new Intl.DateTimeFormat(currentLocale, { ...defaultOptions, ...options }).format(dateObj);
    },

    // 格式化相对时间
    formatRelativeTime: (date: Date | string | number, locale?: string) => {
      const currentLocale = locale || i18n.language;
      const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

      const rtf = new Intl.RelativeTimeFormat(currentLocale, { numeric: 'auto' });

      if (diffInSeconds < 60) {
        return rtf.format(-diffInSeconds, 'second');
      } else if (diffInSeconds < 3600) {
        return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
      } else if (diffInSeconds < 86400) {
        return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
      } else if (diffInSeconds < 2592000) {
        return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
      } else if (diffInSeconds < 31536000) {
        return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
      } else {
        return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
      }
    },
  };
};

export default useI18n;